import { supabase } from '@/lib/supabase';
import { ORDER_STATUSES_WITH_PENDING_STOCK } from '@/lib/orderStatus';

export const calculateStock = async (productId, size, color) => {
  try {
    const { data: inventoryData, error } = await supabase
      .from('inventory')
      .select('quantity')
      .eq('product_id', productId)
      .eq('size', size)
      .eq('color', color);

    if (error) throw error;

    return inventoryData?.reduce((sum, record) => sum + (record.quantity || 0), 0) || 0;
  } catch (error) {
    console.error('Error calculating stock:', error);
    return 0;
  }
};

export const calculateTotalStock = async (productId) => {
  try {
    const { data: inventoryData, error } = await supabase
      .from('inventory')
      .select('quantity')
      .eq('product_id', productId);

    if (error) throw error;

    return inventoryData?.reduce((sum, record) => sum + (record.quantity || 0), 0) || 0;
  } catch (error) {
    console.error('Error calculating total stock:', error);
    return 0;
  }
};

export const calculateProductValue = (stock, price) => {
  return stock * price;
};

export const calculateTotalInventoryValue = async (products) => {
  try {
    let total = 0;
    for (const product of products) {
      const stock = await calculateTotalStock(product.id);
      total += calculateProductValue(stock, product.price);
    }
    return total;
  } catch (error) {
    console.error('Error calculating total inventory value:', error);
    return 0;
  }
};

export const calculatePendingQuantity = async (productId, size, color) => {
  try {
    const { data: orderItems, error } = await supabase
      .from('order_items')
      .select('pending_quantity, orders!inner(status)')
      .eq('product_id', productId)
      .eq('size', size)
      .eq('color', color)
      .in('orders.status', ORDER_STATUSES_WITH_PENDING_STOCK);

    if (error) throw error;

    return orderItems?.reduce((sum, item) => sum + (item.pending_quantity || 0), 0) || 0;
  } catch (error) {
    console.error('Error calculating pending quantity:', error);
    return 0;
  }
};

export const calculateTotalPendingValue = async (productId, price) => {
  try {
    const { data: orderItems, error } = await supabase
      .from('order_items')
      .select('pending_quantity, orders!inner(status)')
      .eq('product_id', productId)
      .in('orders.status', ORDER_STATUSES_WITH_PENDING_STOCK);

    if (error) throw error;

    const totalPendingQuantity = orderItems?.reduce((sum, item) => sum + (item.pending_quantity || 0), 0) || 0;
    return totalPendingQuantity * price;
  } catch (error) {
    console.error('Error calculating total pending value:', error);
    return 0;
  }
};

export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR'
  }).format(amount);
};

export const resetInventory = async () => {
  try {
    await supabase.from('inventory').delete().neq('id', '0');
    await supabase.from('documents').delete().neq('id', '0');
    await supabase.from('document_items').delete().neq('id', '0');
    await supabase.from('orders').delete().neq('id', '0');
    await supabase.from('order_items').delete().neq('id', '0');
  } catch (error) {
    console.error('Error resetting inventory:', error);
    throw error;
  }
};
